import type { ReactElement } from "react";
/**
 * A matchup, drawn rather than spelled out.
 *
 * Two circles carrying the teams' own colours, away first, home second - the
 * order the matchup is written in. On a board of ten signals the pairing is
 * recognisable before any text is read, which is the point: the operator is
 * scanning for a game he already has an opinion about.
 *
 * Team colour is a *secondary* language here. Green, amber and red are
 * reserved for money, so team colour never appears on a figure - only on these
 * circles and, where a logo loads, behind it.
 */

const ESPN_LEAGUE: Record<string, string> = {
  MLB: "mlb",
  NBA: "nba",
  NFL: "nfl",
  NHL: "nhl",
};

/**
 * Team colours, keyed by the id we already store (`mlb:team:st-louis-cardinals`).
 * Only the slug is used, so the same table serves any sport whose ids follow
 * the same shape. A team we have no colour for falls back to a neutral chip
 * rather than an invented hue - a wrong colour is worse than none, because the
 * whole device relies on the pairing being recognisable.
 */
const TEAM_COLOURS: Record<string, string> = {
  // MLB
  "arizona-diamondbacks": "#a71930",
  "atlanta-braves": "#ce1141",
  "baltimore-orioles": "#df4601",
  "boston-red-sox": "#bd3039",
  "chicago-cubs": "#0e3386",
  "chicago-white-sox": "#27251f",
  "cincinnati-reds": "#c6011f",
  "cleveland-guardians": "#00385d",
  "colorado-rockies": "#333366",
  "detroit-tigers": "#0c2340",
  "houston-astros": "#eb6e1f",
  "kansas-city-royals": "#004687",
  "los-angeles-angels": "#ba0021",
  "los-angeles-dodgers": "#005a9c",
  "miami-marlins": "#00a3e0",
  "milwaukee-brewers": "#12284b",
  "minnesota-twins": "#002b5c",
  "new-york-mets": "#002d72",
  "new-york-yankees": "#0c2340",
  "oakland-athletics": "#003831",
  athletics: "#003831",
  "philadelphia-phillies": "#e81828",
  "pittsburgh-pirates": "#fdb827",
  "san-diego-padres": "#2f241d",
  "san-francisco-giants": "#fd5a1e",
  "seattle-mariners": "#0c2c56",
  "st-louis-cardinals": "#c41e3a",
  "tampa-bay-rays": "#092c5c",
  "texas-rangers": "#003278",
  "toronto-blue-jays": "#134a8e",
  "washington-nationals": "#ab0003",
  // NFL. Raiders are silver rather than their black, which would be a hole in
  // the board on a dark ground - the chip has to read as a colour.
  "arizona-cardinals": "#97233f",
  "atlanta-falcons": "#a71930",
  "baltimore-ravens": "#241773",
  "buffalo-bills": "#00338d",
  "carolina-panthers": "#0085ca",
  "chicago-bears": "#0b162a",
  "cincinnati-bengals": "#fb4f14",
  "cleveland-browns": "#ff3c00",
  "dallas-cowboys": "#041e42",
  "denver-broncos": "#fb4f14",
  "detroit-lions": "#0076b6",
  "green-bay-packers": "#203731",
  "houston-texans": "#03202f",
  "indianapolis-colts": "#002c5f",
  "jacksonville-jaguars": "#006778",
  "kansas-city-chiefs": "#e31837",
  "las-vegas-raiders": "#a5acaf",
  "los-angeles-chargers": "#0080c6",
  "los-angeles-rams": "#003594",
  "miami-dolphins": "#008e97",
  "minnesota-vikings": "#4f2683",
  "new-england-patriots": "#002244",
  "new-orleans-saints": "#d3bc8d",
  "new-york-giants": "#0b2265",
  "new-york-jets": "#125740",
  "philadelphia-eagles": "#004c54",
  "pittsburgh-steelers": "#ffb612",
  "san-francisco-49ers": "#aa0000",
  "seattle-seahawks": "#69be28",
  "tampa-bay-buccaneers": "#d50a0a",
  "tennessee-titans": "#4b92db",
  "washington-commanders": "#5a1414",
};

/**
 * ESPN keys its marks by abbreviation, and a game label carries a team name.
 * Without this the URL became ".../mlb/500/milwaukee brewers.png" and every
 * logo silently 404'd, leaving a board of empty circles.
 *
 * NBA and NHL still fall back to the colour chip, which is what an unknown
 * team gets anyway.
 */
const MLB_ABBREVIATIONS: Record<string, string> = {
  "arizona-diamondbacks": "ari",
  "atlanta-braves": "atl",
  "baltimore-orioles": "bal",
  "boston-red-sox": "bos",
  "chicago-cubs": "chc",
  "chicago-white-sox": "chw",
  "cincinnati-reds": "cin",
  "cleveland-guardians": "cle",
  "colorado-rockies": "col",
  "detroit-tigers": "det",
  "houston-astros": "hou",
  "kansas-city-royals": "kc",
  "los-angeles-angels": "laa",
  "los-angeles-dodgers": "lad",
  "miami-marlins": "mia",
  "milwaukee-brewers": "mil",
  "minnesota-twins": "min",
  "new-york-mets": "nym",
  "new-york-yankees": "nyy",
  "oakland-athletics": "oak",
  athletics: "oak",
  "philadelphia-phillies": "phi",
  "pittsburgh-pirates": "pit",
  "san-diego-padres": "sd",
  "san-francisco-giants": "sf",
  "seattle-mariners": "sea",
  "st-louis-cardinals": "stl",
  "tampa-bay-rays": "tb",
  "texas-rangers": "tex",
  "toronto-blue-jays": "tor",
  "washington-nationals": "wsh",
};

const NFL_ABBREVIATIONS: Record<string, string> = {
  "arizona-cardinals": "ari",
  "atlanta-falcons": "atl",
  "baltimore-ravens": "bal",
  "buffalo-bills": "buf",
  "carolina-panthers": "car",
  "chicago-bears": "chi",
  "cincinnati-bengals": "cin",
  "cleveland-browns": "cle",
  "dallas-cowboys": "dal",
  "denver-broncos": "den",
  "detroit-lions": "det",
  "green-bay-packers": "gb",
  "houston-texans": "hou",
  "indianapolis-colts": "ind",
  "jacksonville-jaguars": "jax",
  "kansas-city-chiefs": "kc",
  "las-vegas-raiders": "lv",
  "los-angeles-chargers": "lac",
  "los-angeles-rams": "lar",
  "miami-dolphins": "mia",
  "minnesota-vikings": "min",
  "new-england-patriots": "ne",
  "new-orleans-saints": "no",
  "new-york-giants": "nyg",
  "new-york-jets": "nyj",
  "philadelphia-eagles": "phi",
  "pittsburgh-steelers": "pit",
  "san-francisco-49ers": "sf",
  "seattle-seahawks": "sea",
  "tampa-bay-buccaneers": "tb",
  "tennessee-titans": "ten",
  "washington-commanders": "wsh",
};

/** "Milwaukee Brewers" and "mlb:team:milwaukee-brewers" reduce to the same
 * key, so a label works whether it carries a name or an id. */
export function teamKey(value: string): string {
  return (value.split(":").pop() ?? value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** An abbreviation good enough for a logo URL. Already-short input - "BOS" -
 * is passed through, since other sports supply exactly that. */
export function espnAbbreviation(
  sport: string,
  nameOrId: string,
): string | null {
  const key = teamKey(nameOrId);
  if (sport === "MLB") return MLB_ABBREVIATIONS[key] ?? null;
  if (sport === "NFL") return NFL_ABBREVIATIONS[key] ?? null;
  if (!key.includes("-") && key.length <= 4) return key;
  return null;
}

function slugOf(teamId: string): string {
  return teamKey(teamId);
}

export function teamColour(teamId: string | null | undefined): string | null {
  if (teamId === null || teamId === undefined) return null;
  return TEAM_COLOURS[slugOf(teamId)] ?? null;
}

/**
 * ESPN publishes team marks on a public CDN keyed by abbreviation. When one
 * does not load the circle keeps its colour, so the pairing still reads.
 */
function logoUrl(sport: string, abbreviation: string | null): string | null {
  const league = ESPN_LEAGUE[sport];
  if (league === undefined || abbreviation === null) return null;
  return `https://a.espncdn.com/i/teamlogos/${league}/500/${abbreviation.toLowerCase()}.png`;
}

function TeamMark({
  sport,
  teamId,
  abbreviation,
  name,
}: {
  readonly sport: string;
  readonly teamId: string | null;
  readonly abbreviation: string | null;
  readonly name: string;
}): ReactElement {
  const colour = teamColour(teamId ?? name);
  const src = logoUrl(sport, espnAbbreviation(sport, abbreviation ?? name));
  return (
    <span
      className="team-mark"
      style={colour === null ? undefined : { background: colour }}
      title={name}
    >
      {src === null ? (
        <span className="team-initials">
          {(abbreviation ?? name).slice(0, 3)}
        </span>
      ) : (
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={(event) => {
            // A missing mark must not leave an empty hole. Drop back to the
            // colour chip, which is all the pairing needs.
            event.currentTarget.style.display = "none";
          }}
        />
      )}
    </span>
  );
}

export function Matchup({
  sport,
  awayName,
  homeName,
  awayTeamId = null,
  homeTeamId = null,
  awayAbbreviation = null,
  homeAbbreviation = null,
}: {
  readonly sport: string;
  readonly awayName: string;
  readonly homeName: string;
  readonly awayTeamId?: string | null;
  readonly homeTeamId?: string | null;
  readonly awayAbbreviation?: string | null;
  readonly homeAbbreviation?: string | null;
}): ReactElement {
  // A team's mark sits on that team's own side of the "@", so the side of the
  // fixture being bet is found by position rather than by reading the label.
  // Stacking both marks together on the left made them a decoration; split,
  // they carry which team is which.
  return (
    <span className="matchup">
      <TeamMark
        sport={sport}
        teamId={awayTeamId}
        abbreviation={awayAbbreviation}
        name={awayName}
      />
      <span className="matchup-text">
        {awayName} <span className="matchup-at">@</span> {homeName}
      </span>
      <TeamMark
        sport={sport}
        teamId={homeTeamId}
        abbreviation={homeAbbreviation}
        name={homeName}
      />
    </span>
  );
}
